import matter from 'gray-matter';
import { marked, type Tokens } from 'marked';

/** Mirrors blog `ContentBlock` with an extra Markdown-rendered paragraph variant. */
export type SpotlightContentBlock =
  | {
      type: 'paragraph';
      text: string;
      bold?: string[];
      links?: Array<{ text: string; url: string; target?: string }>;
    }
  | { type: 'paragraphHtml'; html: string }
  | { type: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  | {
      type: 'list';
      listType: 'ordered' | 'unordered';
      items: Array<{
        text: string;
        bold?: string[];
        links?: Array<{ text: string; url: string; target?: string }>;
      }>;
    }
  | { type: 'image'; src: string; alt: string }
  | { type: 'youtube'; url: string; title?: string; caption?: string }
  | {
      type: 'section';
      title: string;
      level: 1 | 2 | 3 | 4 | 5 | 6;
      content: SpotlightContentBlock[];
    };

export interface SpotlightFrontmatter {
  title: string;
  date: string;
  description: string;
  cover: string;
  imageAlt?: string;
  category?: string;
  tags?: string[];
  readTime?: number;
  updated?: string;
  faq?: Array<{ question: string; answer: string }>;
  youtubeLinks?: Array<{ url: string; title?: string }>;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  canonicalUrl?: string;
  ogImage?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isOptionalStringArray(value: unknown): value is string[] | undefined {
  return (
    value === undefined || (Array.isArray(value) && value.every((item) => typeof item === 'string'))
  );
}

function isOptionalFaq(value: unknown): value is SpotlightFrontmatter['faq'] {
  return (
    value === undefined ||
    (Array.isArray(value) &&
      value.every(
        (item) =>
          isRecord(item) && typeof item.question === 'string' && typeof item.answer === 'string'
      ))
  );
}

function isOptionalYoutubeLinks(value: unknown): value is SpotlightFrontmatter['youtubeLinks'] {
  return (
    value === undefined ||
    (Array.isArray(value) &&
      value.every(
        (item) => isRecord(item) && typeof item.url === 'string' && isOptionalString(item.title)
      ))
  );
}

function isSpotlightFrontmatter(value: unknown): value is SpotlightFrontmatter {
  if (!isRecord(value)) return false;

  return (
    typeof value.title === 'string' &&
    typeof value.date === 'string' &&
    typeof value.description === 'string' &&
    typeof value.cover === 'string' &&
    isOptionalString(value.imageAlt) &&
    isOptionalString(value.category) &&
    isOptionalStringArray(value.tags) &&
    (value.readTime === undefined ||
      (typeof value.readTime === 'number' && Number.isFinite(value.readTime))) &&
    isOptionalString(value.updated) &&
    isOptionalFaq(value.faq) &&
    isOptionalYoutubeLinks(value.youtubeLinks) &&
    isOptionalString(value.seoTitle) &&
    isOptionalString(value.seoDescription) &&
    isOptionalStringArray(value.keywords) &&
    isOptionalString(value.canonicalUrl) &&
    isOptionalString(value.ogImage)
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Convert Markdown body to the same block shapes the blog article page expects,
 * plus `paragraphHtml` for rich paragraphs (bold, links, inline code) from Markdown.
 */
export function markdownToSpotlightBlocks(md: string): SpotlightContentBlock[] {
  const tokens = marked.lexer(md.trim());
  const blocks: SpotlightContentBlock[] = [];

  for (const token of tokens) {
    if (token.type === 'space') continue;

    if (token.type === 'heading') {
      const h = token as Tokens.Heading;
      const depth = Math.min(Math.max(h.depth, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6;
      const level = depth === 1 ? 2 : depth;
      blocks.push({ type: 'heading', level, text: h.text });
    } else if (token.type === 'paragraph') {
      const html = marked.parser([token]);
      blocks.push({ type: 'paragraphHtml', html });
    } else if (token.type === 'list') {
      const list = token as Tokens.List;
      const listType = list.ordered ? 'ordered' : 'unordered';
      const items = list.items.map((item) => {
        const li = item as Tokens.ListItem;
        const text = (li.text || '').replace(/\n+/g, ' ').trim();
        return { text };
      });
      blocks.push({ type: 'list', listType, items });
    } else if (token.type === 'image') {
      const im = token as Tokens.Image;
      blocks.push({ type: 'image', src: im.href, alt: im.text || '' });
    } else if (token.type === 'code') {
      const c = token as Tokens.Code;
      const html = `<pre class="overflow-x-auto rounded-lg bg-black/[0.05] p-4 my-6 text-sm"><code>${escapeHtml(c.text)}</code></pre>`;
      blocks.push({ type: 'paragraphHtml', html });
    } else if (token.type === 'table') {
      const html = marked.parser([token]);
      blocks.push({ type: 'paragraphHtml', html });
    } else if (token.type === 'blockquote') {
      const html = marked.parser([token]);
      blocks.push({ type: 'paragraphHtml', html });
    } else if (token.type === 'hr') {
      blocks.push({
        type: 'paragraphHtml',
        html: '<hr class="my-8 border-black/10" />',
      });
    } else if (token.type === 'html') {
      const h = token as Tokens.HTML;
      const raw = h.raw?.trim() || '';
      const yt = raw.match(/(?:youtube\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{6,})/i);
      if (yt?.[1]) {
        blocks.push({
          type: 'youtube',
          url: `https://www.youtube.com/watch?v=${yt[1]}`,
          title: 'YouTube video',
        });
      } else {
        blocks.push({ type: 'paragraphHtml', html: raw });
      }
    }
  }

  return blocks;
}

export function parseSpotlightFile(raw: string, slug: string) {
  const { data, content } = matter(raw);
  const frontmatter: unknown = data;
  if (!isSpotlightFrontmatter(frontmatter)) {
    throw new Error(`Spotlight ${slug}: frontmatter requires title, date, description, cover`);
  }
  const fm = frontmatter;

  const bodyContent = markdownToSpotlightBlocks(content);

  const publishedAt = new Date(fm.date).toISOString();
  const _updatedAt = fm.updated ? new Date(fm.updated).toISOString() : publishedAt;

  const categoryTitle = fm.category || 'Spotlight';
  const readTime =
    fm.readTime ?? Math.max(1, Math.round(content.split(/\s+/).filter(Boolean).length / 200));

  return {
    _id: slug,
    slug,
    title: fm.title,
    excerpt: fm.description,
    body: [],
    bodyContent,
    image: fm.cover,
    imageAlt: fm.imageAlt || fm.title,
    publishedAt,
    _updatedAt,
    categories: [{ title: categoryTitle }],
    tags: fm.tags || [],
    readTime,
    faq: fm.faq || [],
    youtubeLinks: fm.youtubeLinks || [],
    seoTitle: fm.seoTitle || `${fm.title} | Veeso Spotlight`,
    seoDescription: fm.seoDescription || fm.description,
    keywords: fm.keywords || [],
    canonicalUrl: fm.canonicalUrl || `https://veeso.ai/spotlight/${slug}`,
    ogImage: fm.ogImage || fm.cover,
    relatedPosts: [] as Array<{
      _id: string;
      slug: string;
      title: string;
      excerpt?: string;
      image?: string;
      publishedAt?: string;
    }>,
  };
}
